import type { Input } from "@/workflow_api/types/workflow";
import { NodeTypes } from "@/workflow_api/types/workflow";
import type {
  LogicalOperator,
  SerializeValueType,
  SubConditionData,
  Variable,
} from "../types";
import type { NodeVariable } from "../../types";

export const getValueType = (type: SerializeValueType) => {
  if (!type) return "NONE";
  switch (type) {
    case "string":
    case "regex":
      return "STRING";
    case "positive-integer":
    case "number":
      return "NUMBER";
    case "variable":
      return "MODEL";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "array":
      return "ARRAY";
    default:
      return "NONE";
  }
};

/**
 * Check if a variable is from an interoperability node
 */
const nodeReturnsObject = (
  variable: Variable,
  nodeVariables?: NodeVariable[],
): boolean => {
  if (!nodeVariables || !variable.node_id) {
    return false;
  }

  const nodeVariable = nodeVariables.find(
    (nv) => nv.nodeId === variable.node_id,
  );

  return (
    nodeVariable?.type === NodeTypes.INTEROPERABILITY ||
    nodeVariable?.type === NodeTypes.N8N
  );
};

const getSerializedVariable = (
  value: string | Variable | null | string[],
  fallbackType: SerializeValueType,
  constantNum: number,
  nodeVariables?: NodeVariable[],
  isArrayItem = false,
): Input[number] => {
  if (value === null) {
    return {
      name: "var_if_" + constantNum,
      type: getValueType(fallbackType),
      value: null,
    };
  }

  console.log(value);

  // Manejar arrays
  if (Array.isArray(value)) {
    const simpleValues: string[] = [];
    const variableReferences: Input[number][] = [];

    value.forEach((item, index) => {
      try {
        const parsed = JSON.parse(item);
        // If it can be parsed as JSON and has an id, it is a variable
        if (parsed && typeof parsed === "object" && parsed.id) {
          const serializedVar = getSerializedVariable(
            parsed as Variable,
            fallbackType === "array" ? "string" : fallbackType,
            constantNum + index,
            nodeVariables,
            true,
          );
          // Store order in output_name field
          variableReferences.push({
            ...serializedVar,
            output_name: String(index),
          });
        } else {
          simpleValues.push(item);
        }
      } catch {
        simpleValues.push(item);
      }
    });

    const result: Input[number] = {
      name: "var_if_" + constantNum,
      type: getValueType("array"),
      value: simpleValues,
    };
    if (variableReferences.length > 0) {
      result.references = variableReferences;
    }
    return result;
  }

  if (fallbackType === "array" && typeof value === "string") {
    const array = value.split(",").map((item) => item.trim());
    return {
      name: "var_if_" + constantNum,
      type: getValueType(fallbackType),
      value: array,
    };
  }

  if (typeof value === "string") {
    return {
      name: "var_if_" + constantNum,
      type: getValueType(fallbackType),
      value,
    };
  }
  const variable = value as Variable;

  if (variable.global_type) {
    if (variable.node_id === "global-variables") {
      return {
        name: "var_if_" + constantNum,
        type: getValueType(variable.type ?? fallbackType),
        value: variable.id || null,
        global_type: variable.global_type,
      };
    }
    return {
      name: "var_if_" + constantNum,
      type: getValueType(variable.type ?? fallbackType),
      value: variable.node_id || null,
      global_type: variable.global_type,
    };
  }

  if (variable.id === variable.node_id) {
    return {
      name: "var_if_" + constantNum,
      type: getValueType((value as Variable).type ?? fallbackType),
      value: null,
      connection: (value as Variable).node_id,
    };
  }

  const serializedInput: Input[number] = {
    name: "var_if_" + constantNum,
    type: getValueType(variable.type ?? fallbackType),
    value: null,
    connection: variable.node_id,
  };

  // For interoperability nodes: don't set field (it should be a UUID, but we don't have one),
  // instead set output_name to the endpoint field key (variable.id)
  // For other nodes: set field to variable.id (which is a UUID)

  if (nodeReturnsObject(variable, nodeVariables)) {
    // Interoperability/N8N node: use output_name instead of field
    serializedInput.output_name = variable.output_name; // endpoint field key like "last_name"
  } else {
    //case for expanded variables from array
    if (variable.field_id && variable.output_name) {
      serializedInput.field = variable.field_id;
      serializedInput.output_name = variable.output_name;
    } else if (variable.ref_name === "global-variables") {
      serializedInput.value = variable.id;
      serializedInput.global_type = variable.global_type;
      serializedInput.connection = undefined;
    } else {
      // Regular node (form, etc.): use field with UUID
      serializedInput.field = variable.id;
      if (isArrayItem) serializedInput.field_id = variable.id;
    }
  }

  return serializedInput;
};

/**
 * Parse the dynamic Condition information to a formated object to be saved
 * Notes:
 * 1. The first input of a condition is always a variable
 * 2. The second input can ve a variable or a const
 *    2.1 If it is a const, always will be the first const
 * TODO: add connection nodes
 * @param param0
 * @param refName
 * @returns
 */
export const serializeIfNode = (
  conditionsData: SubConditionData[],
  logicalOperator: LogicalOperator,
  nodeVariables?: NodeVariable[],
): Input => {
  const conditions = conditionsData.map((condition) => {
    const inputs: Input = [];
    inputs.push(
      getSerializedVariable(
        condition.variable,
        condition.operation?.comparativeValueType,
        inputs.length + 1,
        nodeVariables,
      ),
    );
    if (condition.operation?.comparativeValueType) {
      inputs.push(
        getSerializedVariable(
          condition.comparativeValue,
          condition.operation?.comparativeValueType,
          inputs.length + 1,
          nodeVariables,
        ),
      );
    }
    return {
      name: condition.id || "",
      type: condition.operation?.name ?? "",
      operator: logicalOperator.toUpperCase(),
      inputs,
    };
  });

  const result = [
    {
      name: "condition_1",
      value: null,
      type: "CONDITION",
      conditions,
    },
  ];

  return result;
};
